import React, { useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { Page, Typography } from '@occc-app/design-system';
import { DateTimeFormat, DurationUnit, formatDateTime } from '@occc-app/lib/date';
import { useGetMetricCounts } from '@occc-app/dataAccessLayer/metrics/queries';
import { Interval as GraphQLInterval } from '@occc/graphql';
import { getDateRanges, DateRangeSelector, RangeTypeWithCustom } from '@occc/ui-design-system';

import { CoreProgramMetricChart } from './components/CoreProgramMetricChart';
import { CoreProgramSnapshots } from './components/CoreProgramSnapshots';
import { RevenueSnapshots } from './components/RevenueSnapshots';
import { MetricType } from './types';
import {
  DEFAULT_METRIC_END_DATE,
  DEFAULT_INTERVAL,
  DEFAULT_METRIC_TYPE,
  DEFAULT_PLOT_CUMULATIVE,
  DEFAULT_METRIC_START_DATE,
} from './constants';
import { getIntervalFromRangeType, getWelcomeMessage, metricParser } from './utils';
import { useChainId } from '../Chain/hooks';

const DashboardPage = () => {
  const chainId = useChainId();

  const [metricsStartDate, setMetricsStartDate] = useState<Date>(DEFAULT_METRIC_START_DATE);
  const [metricsEndDate, setMetricsEndDate] = useState<Date>(DEFAULT_METRIC_END_DATE);
  const [metricsInterval, setMetricsInterval] = useState<DurationUnit>(DEFAULT_INTERVAL);
  const [selectedMetricType, setSelectedMetricType] = useState<MetricType>(DEFAULT_METRIC_TYPE);
  const [plotCumulative, setPlotCumulative] = useState<boolean>(DEFAULT_PLOT_CUMULATIVE);

  const dateFormat =
    metricsInterval === 'hour' ? DateTimeFormat.Default : DateTimeFormat.DateDashed;
  const interval = metricsInterval === 'hour' ? GraphQLInterval.Hour : GraphQLInterval.Day;

  const dateRanges = useMemo(() => getDateRanges(), []);

  const { loading, data } = useGetMetricCounts({
    variables: {
      filter: {
        chainIdEquals: chainId,
        dateMin: formatDateTime(metricsStartDate, dateFormat),
        dateMax: formatDateTime(metricsEndDate, dateFormat),
        interval,
      },
    },
  });

  const parsedMetrics = useMemo(
    () => metricParser(data?.metricCounts ?? [], metricsStartDate, metricsEndDate, metricsInterval),
    [data?.metricCounts, metricsStartDate, metricsEndDate, metricsInterval],
  );
  const isNoteVisible = metricsInterval === 'hour' && selectedMetricType === 'transactions';

  const onDateRangeChange = (
    value: { startDate: Date; endDate: Date },
    type: RangeTypeWithCustom,
  ) => {
    const interval = getIntervalFromRangeType(type);

    setMetricsStartDate(value.startDate);
    setMetricsEndDate(value.endDate);
    setMetricsInterval(interval);

    if (type === 'last_365_days') {
      setPlotCumulative(true);
    } else {
      setPlotCumulative(false);
    }
  };

  return (
    <Page
      browserTabTitle='Dashboard'
      pageHeaderProps={{
        title: 'Dashboard',
        titleTestId: 'dashboard-title',
        subtitle: getWelcomeMessage(),
      }}
      actions={
        <DateRangeSelector
          value={{ startDate: metricsStartDate, endDate: metricsEndDate }}
          onChange={onDateRangeChange}
          dateRangePresets={[
            dateRanges.today,
            dateRanges.yesterday,
            { ...dateRanges.last_7_days, label: 'Previous 7 Days' },
            { ...dateRanges.last_30_days, label: 'Previous 30 Days' },
            dateRanges.last_365_days,
          ]}
        />
      }
    >
      <Card sx={{ mb: 3 }}>
        <CoreProgramSnapshots
          metrics={parsedMetrics}
          metricType={selectedMetricType}
          onMetricTypeChange={setSelectedMetricType}
        />
        <CoreProgramMetricChart
          metrics={parsedMetrics}
          metricsLoading={loading}
          startDate={metricsStartDate}
          endDate={metricsEndDate}
          plotCumulative={plotCumulative}
          metricType={selectedMetricType}
          interval={metricsInterval}
        />

        {isNoteVisible && (
          <Typography variant='button' paragraph align='center'>
            {`Please note, hourly Loyalty Transactions are updated every 10 minutes.`}
          </Typography>
        )}
      </Card>
      <Card>
        <RevenueSnapshots metrics={parsedMetrics} interval={metricsInterval} />
      </Card>
    </Page>
  );
};

export default DashboardPage;
